import * as React from 'react';

import { Unit, enumUnit } from '@rematter/frontend-lib-api-client';
import { convertFromPounds, convertToPounds } from '@rematter/ui-web';
import Big from 'big.js';
import partition from 'lodash/partition';

import {
  calculateLineItemsTotal,
  calculateTotalEachCountFromLoadItems,
  calculateTotalNetWeightFromLoadItems,
  createGetFooterDisplayUnit,
  getCommonPricingUnitFromLoadItems,
  isEachItem,
} from '../utils/weight';

import {
  enumLoadWeightType,
  LoadFormLineItem,
  WeightUnitType,
} from '~/modules/trade/types/load';

const convertLineItemWeightToPounds = (
  value: number | null | undefined,
  unit: string | null | undefined,
): number => {
  if (!value || !unit) return 0;

  try {
    // Use Unit directly - no conversion needed
    return convertToPounds(value, unit as Unit);
  } catch (error) {
    // If unit conversion fails, assume pounds
    console.warn(`Failed to convert unit ${unit}, assuming pounds:`, error);
    return value;
  }
};

type Props = {
  lineItems: LoadFormLineItem[];
  facilityDefaultUnit: Unit;
  weightUnit?: WeightUnitType;
  getFooterDisplayUnit?: (pricingUnit: Unit) => Unit;
};

export const useLoadLineItemsTotals = ({
  lineItems,
  facilityDefaultUnit,
  weightUnit = 'price',
  getFooterDisplayUnit: customGetFooterDisplayUnit,
}: Props) =>
  React.useMemo(() => {
    /*
     * Derive getFooterDisplayUnit from weightUnit if not provided
     */
    const getFooterDisplayUnit =
      customGetFooterDisplayUnit ??
      createGetFooterDisplayUnit(weightUnit, facilityDefaultUnit);

    /*
     * Separate "Each" items from weight-based items
     */
    const [eachLineItems, weightLineItems] = partition(lineItems, isEachItem);

    /*
     * Totals for "Each" items using reusable helper
     */
    const totalEachCount = calculateTotalEachCountFromLoadItems(lineItems);
    const netEachOwed = eachLineItems
      .reduce(
        (sum, item) =>
          sum.plus(item.weights?.[enumLoadWeightType.QuantityOwed]?.value || 0),
        new Big(0),
      )
      .toNumber();
    const requestedEachCount = eachLineItems
      .reduce(
        (sum, item) =>
          sum.plus(item.weights?.[enumLoadWeightType.Net]?.value || 0),
        new Big(0),
      )
      .toNumber();

    /*
     * Determine the common pricing unit using reusable helper
     */
    const pricingUnit = getCommonPricingUnitFromLoadItems(
      lineItems,
      facilityDefaultUnit,
    );

    /*
     * Always convert each item's weight to pounds using its actual unit, then sum
     * This ensures we respect the actual netWeight.unit field, not just the pricingUnit
     */
    const netWeightValueInPounds = weightLineItems
      .reduce(
        (sum, item) =>
          sum.plus(
            convertLineItemWeightToPounds(
              new Big(
                item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
              ).toNumber(),
              item.weights?.[enumLoadWeightType.Net]?.unit ?? enumUnit.Pounds,
            ),
          ),
        new Big(0),
      )
      .toNumber();
    const netWeightOwedValueInPounds = weightLineItems
      .reduce(
        (sum, item) =>
          sum.plus(
            convertLineItemWeightToPounds(
              new Big(
                item.weights?.[enumLoadWeightType.QuantityOwed]?.value ?? 0,
              ).toNumber(),
              item.weights?.[enumLoadWeightType.QuantityOwed]?.unit ??
                enumUnit.Pounds,
            ),
          ),
        new Big(0),
      )
      .toNumber();

    const requestedWeightValueInPounds = weightLineItems
      .reduce(
        (sum, item) =>
          sum.plus(
            convertLineItemWeightToPounds(
              new Big(
                item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
              ).toNumber(),
              item.weights?.[enumLoadWeightType.Net]?.unit ?? enumUnit.Pounds,
            ),
          ),
        new Big(0),
      )
      .toNumber();

    /*
     * Convert the totals from pounds to the pricing unit for display
     */
    const netWeightValue =
      netWeightValueInPounds > 0
        ? convertFromPounds(netWeightValueInPounds, pricingUnit)
        : 0;
    const netWeightOwedValue =
      netWeightOwedValueInPounds > 0
        ? convertFromPounds(netWeightOwedValueInPounds, pricingUnit)
        : 0;
    const requestedWeightValue =
      requestedWeightValueInPounds > 0
        ? convertFromPounds(requestedWeightValueInPounds, pricingUnit)
        : 0;

    /*
     * Determine the display unit for footer based on toggle
     */
    const footerDisplayUnit = getFooterDisplayUnit(pricingUnit);
    const needsConversion = footerDisplayUnit !== pricingUnit;

    /*
     * Convert values if display unit differs from pricing unit
     */
    const displayNetWeightValue =
      needsConversion && netWeightValue > 0
        ? convertFromPounds(
            convertToPounds(netWeightValue, pricingUnit),
            footerDisplayUnit,
          )
        : netWeightValue;

    const displayNetWeightOwedValue =
      needsConversion && netWeightOwedValue > 0
        ? convertFromPounds(
            convertToPounds(netWeightOwedValue, pricingUnit),
            footerDisplayUnit,
          )
        : netWeightOwedValue;

    const displayRequestedWeightValue =
      needsConversion && requestedWeightValue > 0
        ? convertFromPounds(
            convertToPounds(requestedWeightValue, pricingUnit),
            footerDisplayUnit,
          )
        : requestedWeightValue;

    /*
     * Calculate totalNetWeight using reusable helper
     */
    const totalNetWeight = calculateTotalNetWeightFromLoadItems(
      lineItems,
      footerDisplayUnit,
    );

    /*
     * Calculate estimated price based on requestedWeight to match line item logic
     */
    const totalEstimatedPrice = calculateLineItemsTotal(lineItems);

    return {
      itemsCount: lineItems.length,
      netWeight:
        displayNetWeightValue > 0
          ? {
              value: displayNetWeightValue,
              unit: footerDisplayUnit,
              __typename: 'Weight' as const,
            }
          : null,
      netWeightOwed:
        displayNetWeightOwedValue > 0
          ? {
              value: displayNetWeightOwedValue,
              unit: footerDisplayUnit,
              __typename: 'Weight' as const,
            }
          : null,
      requestedWeight:
        displayRequestedWeightValue > 0
          ? {
              value: displayRequestedWeightValue,
              unit: footerDisplayUnit,
              __typename: 'Weight' as const,
            }
          : null,

      netEachOwed,
      requestedEachCount,
      pricingUnit: footerDisplayUnit,
      totalEachCount,
      totalNetWeight,

      totalEstimatedPrice,
    };
  }, [lineItems, customGetFooterDisplayUnit, weightUnit, facilityDefaultUnit]);
